import { ReactNode, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import LoadingButton from 'atoms/buttons/LoadingButton';
import { Container } from 'atoms/layout/Container';
import { Cards, Layout } from 'components/cardswap/Layout';
import { Header } from 'components/cardswap/Layout/Header';
import { StepProps } from 'components/cardswap/types';
import useCompleteAndNextStep from 'lib/steps/useCompleteAndNextStep';

type Props = {
  cardOptions: string[];
  submit: (cardSlug: string) => Promise<void>;
  renderCard: (
    cardOption: string,
    {
      isDismissed,
      isSelected,
      onSelect,
      onDisplayPlayerInfo,
    }: {
      isDismissed: boolean;
      isSelected: boolean;
      onSelect: () => void;
      onDisplayPlayerInfo: () => void;
    }
  ) => ReactNode;
  drawerContent?: (cardSlug: string) => ReactNode | undefined;
} & StepProps;

export const PickCardToSwap = ({
  cardOptions,
  renderCard,
  submit,
  nextStep,
  drawerContent,
}: Props) => {
  const { formatMessage } = useIntl();
  const [focusedCardSlug, setFocusedCardSlug] = useState<string>();
  const [selectedSlug, setSelectedSlug] = useState<string | null>(null);

  const { submitting, complete: completeCardSwapTask } = useCompleteAndNextStep(
    submit,
    selectedSlug,
    nextStep
  );

  return (
    <Layout
      main={
        <>
          <Header
            title={formatMessage({
              id: 'CardSwap.PickCardToSwap.title',
              defaultMessage: 'Select a Card to trade in',
            })}
            subtitle={formatMessage({
              id: 'CardSwap.PickCardToSwap.subtitle2',
              defaultMessage: `You'll get a Card of close to equal value`,
            })}
          />

          <Container>
            <Cards className="two three four five">
              {cardOptions.map(slug => {
                return renderCard(slug, {
                  isSelected: selectedSlug === slug,
                  isDismissed: selectedSlug !== null && selectedSlug !== slug,
                  onSelect: () => {
                    setSelectedSlug(slug);
                  },
                  onDisplayPlayerInfo: () => {
                    setFocusedCardSlug(slug);
                  },
                });
              })}
            </Cards>
          </Container>
        </>
      }
      footer={
        <LoadingButton
          color="tertiary"
          onClick={completeCardSwapTask}
          disabled={selectedSlug === null}
          loading={submitting}
          size="large"
        >
          <FormattedMessage
            id="CardSwap.PickCardToSwap.tradeInCta"
            defaultMessage="Trade in"
          />
        </LoadingButton>
      }
      focusedSlug={focusedCardSlug}
      setFocusedSlug={setFocusedCardSlug}
      drawerContent={drawerContent?.(focusedCardSlug!)}
    />
  );
};
